const API_URL =
  process.env.NEXT_PUBLIC_API_URL ||
  (process.env.NODE_ENV === "development" ? "http://localhost:4000" : "");

let accessToken: string | null = null;
let refreshPromise: Promise<string | null> | null = null;

export { API_URL };

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const res = await fetch(`${API_URL}/auth/refresh`, {
      method: "POST",
      credentials: "include",
    });

    if (!res.ok) {
      setAccessToken(null);
      return null;
    }

    const data = (await res.json()) as { accessToken: string };
    setAccessToken(data.accessToken);
    return data.accessToken;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

type ApiFetchOptions = Omit<RequestInit, "headers"> & {
  headers?: Record<string, string>;
  retryOn401?: boolean;
};

function isFormDataBody(body: any) {
  return typeof FormData !== "undefined" && body instanceof FormData;
}

function buildHeaders(
  options: ApiFetchOptions,
  token: string | null,
  usingFormData: boolean,
) {
  const headers: Record<string, string> = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  if (!usingFormData && !headers["Content-Type"] && !headers["content-type"]) {
    headers["Content-Type"] = "application/json";
  }

  return headers;
}

async function doRequest(path: string, options: ApiFetchOptions = {}) {
  const url = path.startsWith("http") ? path : `${API_URL}${path}`;
  const body = options.body as any;
  const usingFormData = isFormDataBody(body);

  let token = getAccessToken();
  let headers = buildHeaders(options, token, usingFormData);

  let res = await fetch(url, {
    ...options,
    credentials: "include",
    headers,
  });

  if (res.status === 401 && options.retryOn401 !== false) {
    const newToken = await refreshAccessToken();
    if (!newToken) throw new Error("Unauthorized");

    token = newToken;
    headers = buildHeaders(options, token, usingFormData);

    res = await fetch(url, {
      ...options,
      credentials: "include",
      headers,
    });
  }

  return res;
}

export async function apiFetch<T>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const res = await doRequest(path, options);

  if (!res.ok) throw new Error(await safeErr(res));
  if (res.status === 204) return undefined as T;

  return (await res.json()) as T;
}

export async function apiFetchBlob(
  path: string,
  options: ApiFetchOptions = {},
): Promise<Blob> {
  const res = await doRequest(path, options);

  if (!res.ok) throw new Error(await safeErr(res));
  return await res.blob();
}

export async function apiFetchResponse(
  path: string,
  options: ApiFetchOptions = {},
): Promise<Response> {
  const res = await doRequest(path, options);

  if (!res.ok) throw new Error(await safeErr(res));
  return res;
}

async function safeErr(res: Response) {
  try {
    const data = await res.json();
    return data?.error || `${res.status} ${res.statusText}`;
  } catch {
    return `${res.status} ${res.statusText}`;
  }
}
